/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { type ReactNode } from 'react';
import { css, jsx } from '@compiled/react';
import { FabricElementsAnalyticsContext } from '@atlaskit/analytics-namespaced-context';
import { messages } from './i18n';
import { token } from '@atlaskit/tokens';

import { useIntl } from 'react-intl';

const listStyles = css({
	listStyleType: 'none',
	paddingLeft: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'div + div': {
		marginTop: token('space.050'),
	},
});

export interface Props {
	children?: ReactNode;
	listId?: string;
}

const TaskList = ({ listId, children }: Props): JSX.Element | null => {
	const listSize = React.Children.count(children);

	const { formatMessage } = useIntl();

	if (!children) {
		return null;
	}

	// Data attributes are required for copy and paste from rendered content
	// to the editor to preserve the task.
	// This allows the editor to differentiate between numbered and ordered lists,
	// and action items, which all share the common '‹li›' element.
	// The value of 'data-task-local-id' should be discarded upon paste, with a
	// a new uuid generated by the editor for the cloned content.
	return (
		<div
			role="group"
			css={listStyles}
			data-task-list-local-id=""
			aria-label={formatMessage(messages.fieldsetLabel)}
		>
			{React.Children.map(children, (child, idx) => {
				return (
					<FabricElementsAnalyticsContext
						data={{
							listLocalId: listId,
							listSize,
							position: idx,
						}}
					>
						{child}
					</FabricElementsAnalyticsContext>
				);
			})}
		</div>
	);
};

export default TaskList;
